import { Link } from "wouter";
import { useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowLeft, Phone, ChevronRight, Search, BookOpen, HelpCircle, Star } from "lucide-react";
import { trackWhatsAppClick, trackPhoneClick } from "@/lib/tracking";

const BRAND_GOLD = "#D4A843";
const WHATSAPP_NUMBER = "971545846604";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("مرحباً، أحتاج مساعدة في الوصول لصفحة معينة.")}`;
const CLINIC_PHONE = "+971 4 296 4442";

export default function NotFound() {
  const { t, isRtl } = useLanguage();

  // Set noindex meta tag for 404 pages so search engines don't index them
  useEffect(() => {
    // Set robots meta to noindex
    let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", "noindex, nofollow");

    // Update title
    document.title = "404 - Page Not Found | Bella Rose Medical Center";

    // Cleanup: restore indexing when navigating away
    return () => {
      if (robotsMeta) {
        robotsMeta.setAttribute("content", "index, follow");
      }
    };
  }, []);

  // Popular pages to suggest
  const suggestedPages = [
    { href: "/services", icon: Star, labelEn: "Our Services", labelAr: "خدماتنا" },
    { href: "/faq", icon: HelpCircle, labelEn: "FAQ", labelAr: "الأسئلة الشائعة" },
    { href: "/blog", icon: BookOpen, labelEn: "Blog", labelAr: "المدونة" },
    { href: "/guides", icon: Search, labelEn: "Treatment Guides", labelAr: "دليل العلاجات" },
  ];

  return (
    <div className="min-h-screen bg-[#FBF8F1] flex items-center justify-center" dir={isRtl ? "rtl" : "ltr"}>
      <div className="text-center px-6 max-w-lg">
        <p className="text-[120px] md:text-[180px] font-serif font-light leading-none" style={{ color: BRAND_GOLD }}>404</p>
        <h1 className="font-serif text-2xl md:text-3xl text-gray-900 mb-4 -mt-4">
          {t("Page Not Found", "الصفحة غير موجودة")}
        </h1>
        <p className="text-gray-500 text-sm mb-8 leading-relaxed">
          {t(
            "The page you're looking for doesn't exist or has been moved. Let us help you find what you need.",
            "الصفحة التي تبحث عنها غير موجودة أو تم نقلها. دعنا نساعدك في إيجاد ما تحتاج."
          )}
        </p>

        {/* Suggested Pages */}
        <h2 className="font-serif text-xl text-gray-900 mb-4">
          {t("Explore Our Services", "استكشف خدماتنا")}
        </h2>
        <div className="grid grid-cols-2 gap-3 mb-8">
          {suggestedPages.map((page) => (
            <Link
              key={page.href}
              href={page.href}
              className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 hover:border-[#D4A843] hover:bg-[#D4A843]/5 transition-all text-sm text-gray-700 hover:text-gray-900"
            >
              <page.icon className="w-4 h-4 text-[#D4A843] shrink-0" />
              <span>{t(page.labelEn, page.labelAr)}</span>
            </Link>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <Link href="/" className="inline-flex items-center justify-center gap-2 text-gray-900 font-bold text-xs tracking-[0.15em] uppercase px-8 py-4 hover:brightness-95 transition-colors" style={{ backgroundColor: BRAND_GOLD }}>
            <ArrowLeft className="w-4 h-4" />
            {t("BACK TO HOME", "العودة للرئيسية")}
          </Link>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 border border-gray-300 text-gray-900 text-xs tracking-[0.15em] uppercase px-8 py-4 hover:bg-[#D4A843]/10 transition-all" onClick={() => trackWhatsAppClick("not_found")}>
            {t("CONTACT US", "تواصل معنا")}
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
        <div className="flex items-center justify-center gap-6 text-gray-400 text-xs">
          <Link href="/offers" className="hover:text-gray-900 transition-colors">{t("Offers", "العروض")}</Link>
          <span>|</span>
          <Link href="/reviews" className="hover:text-gray-900 transition-colors">{t("Reviews", "التقييمات")}</Link>
          <span>|</span>
          <a href={`tel:${CLINIC_PHONE}`} className="hover:text-gray-900 transition-colors flex items-center gap-1" onClick={() => trackPhoneClick("not_found")}>
            <Phone className="w-3 h-3" />
            {t("Call", "اتصل")}
          </a>
        </div>
      </div>
    </div>
  );
}
